import { getFormTabErrorCounts } from '../../../../../lib/formTabErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, GitBranch, Link2, ShieldCheck } from 'lucide-react';
import { useForm, type FieldErrors } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
} from '@shigo-ui/dashboard-ui';
import { Button, Form } from '@shigo-ui/ui';

import { EditorSectionTabs } from '../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../components/forms/useEditorSection';
import { dashboardT } from '../../../../../lib/messages';
import { ownerFormSchema, type OwnerFormValue } from '../../formSchema';
import { OwnerEditorTab } from '../../model';
import { OwnerComplianceFields } from './OwnerComplianceFields';
import { OwnerIdentityFields } from './OwnerIdentityFields';
import { OwnerPartnersFields } from './OwnerPartnersFields';
import { OwnerSchainFields } from './OwnerSchainFields';

const tabs = [
  { value: OwnerEditorTab.Identity, icon: Building2, label: 'owners.tabs.identity' },
  { value: OwnerEditorTab.Schain, icon: GitBranch, label: 'owners.tabs.schain' },
  { value: OwnerEditorTab.Compliance, icon: ShieldCheck, label: 'owners.tabs.compliance' },
  { value: OwnerEditorTab.Partners, icon: Link2, label: 'owners.tabs.partners' },
] as const;

export function OwnerEditorForm(props: {
  initialValue: OwnerFormValue;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (value: OwnerFormValue) => Promise<void>;
}) {
  const [editorSection, setActiveTab] = useEditorSectionControls(
    OwnerEditorTab.Identity,
    Object.values(OwnerEditorTab),
  );
  const form = useForm<OwnerFormValue>({
    defaultValues: props.initialValue,
    resolver: zodResolver(ownerFormSchema),
    mode: 'onSubmit',
  });
  function invalidSubmit(errors: FieldErrors<OwnerFormValue>) {
    if (errors.billingContactEmail || errors.tcfVendorId) {
      setActiveTab(OwnerEditorTab.Compliance);
    } else if (errors.resellerBundleAllowlist) {
      setActiveTab(OwnerEditorTab.Schain);
    } else {
      setActiveTab(OwnerEditorTab.Identity);
    }
  }

  const tabErrors = getFormTabErrorCounts(
    form.formState.errors,
    {
      [OwnerEditorTab.Schain]: ['defaultSchainLogicMode', 'resellerBundleAllowlist'],
      [OwnerEditorTab.Compliance]: [
        'billingContactEmail',
        'tcfVendorId',
        'billingEntity',
        'paymentCycle',
      ],
    },
    OwnerEditorTab.Identity,
  );

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={(event) => void form.handleSubmit(props.onSubmit, invalidSubmit)(event)}
      >
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList>
            {tabs.map(({ value, label, icon: Icon }) => (
              <DashboardPageFormTab
                key={value}
                value={value}
                errorCount={tabErrors[value]}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
                icon={<Icon className="h-4 w-4" aria-hidden="true" />}
              >
                {dashboardT(label)}
              </DashboardPageFormTab>
            ))}
          </DashboardPageFormTabList>
          {tabs.map(({ value }) => (
            <DashboardPageFormTabPanel key={value} value={value}>
              <OwnerEditorFields tab={value} />
            </DashboardPageFormTabPanel>
          ))}
        </EditorSectionTabs>
        <DashboardPageFormActions>
          <Button type="button" variant="outline" disabled={props.saving} onClick={props.onCancel}>
            {dashboardT('common.cancel')}
          </Button>
          <Button type="submit" disabled={props.saving}>
            {dashboardT(props.saving ? 'common.saving' : 'common.save')}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}

function OwnerEditorFields({ tab }: { tab: OwnerEditorTab }) {
  if (tab === OwnerEditorTab.Schain) return <OwnerSchainFields />;
  if (tab === OwnerEditorTab.Compliance) return <OwnerComplianceFields />;
  if (tab === OwnerEditorTab.Partners) return <OwnerPartnersFields />;
  return <OwnerIdentityFields />;
}
